import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { type Channel as StreamChannelType, StreamChat } from 'stream-chat'

import { getBugsnag } from '@/utils/bugsnag.client'
import { streamKeys } from './stream.keys'
import {
  shouldRetryStreamBootstrapError,
  STREAM_BOOTSTRAP_REFRESH_MS,
  StreamBootstrapAuthorizationError,
  StreamBootstrapTransientError,
  toStreamBootstrapError,
} from '../streamBootstrap'
import { getStreamUnreadRetryDelay } from '../streamUnreadRetry'

function reportOrderStreamUnreadFailure(scope: 'bootstrap' | 'sync') {
  getBugsnag()?.notify(
    new Error('Order Stream unread synchronization failed'),
    (event) => {
      event.addMetadata('streamUnread', { scope: `order-${scope}` })
    },
  )
}

type StreamBootstrap = {
  apiKey: string
  token: string
  tokenExpiresAt: number
  user: {
    id: string
    name: string
  }
  channel: {
    id: string
    type: 'messaging'
  }
}

async function bootstrapOrderChannel(
  orderID: string,
): Promise<StreamBootstrap> {
  let response: Response
  try {
    response = await fetch(`/api/stream/orders/${orderID}`, {
      method: 'POST',
      cache: 'no-store',
    })
  } catch {
    throw new StreamBootstrapTransientError()
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      error?: string
    } | null

    throw toStreamBootstrapError(response.status, payload?.error)
  }

  return (await response.json()) as StreamBootstrap
}

export function useOrderStreamHasUnread({
  orderID,
  userID,
  enabled: streamUnreadEnabled = true,
}: {
  orderID: string | undefined
  /** Required for cache key: bootstrap token is scoped to the signed-in user. */
  userID: string | undefined
  /** When false, skips bootstrap + Stream client (e.g. order message tab already opens chat). */
  enabled?: boolean
}) {
  const [unreadState, setUnreadState] = useState<{
    scope: string | null
    value: boolean | null
  }>({ scope: null, value: null })
  const retryAttemptRef = useRef(0)
  const retrySessionRef = useRef<string | null>(null)
  const terminalSessionRef = useRef<string | null>(null)
  const reportedBootstrapErrorRef = useRef<unknown>(undefined)
  const [retryGeneration, setRetryGeneration] = useState(0)

  const queryEnabled = Boolean(orderID && userID) && streamUnreadEnabled
  const unreadScope = queryEnabled ? `${userID}:${orderID}` : null

  const { data: bootstrap, error: bootstrapError } = useQuery({
    queryKey: streamKeys.orderUnread(userID, orderID),
    queryFn: async () => bootstrapOrderChannel(orderID as string),
    enabled: queryEnabled,
    retry: shouldRetryStreamBootstrapError,
    retryDelay: (attemptIndex) => 1500 * (attemptIndex + 1),
    refetchOnWindowFocus: false,
    staleTime: STREAM_BOOTSTRAP_REFRESH_MS,
    refetchInterval: STREAM_BOOTSTRAP_REFRESH_MS,
    refetchIntervalInBackground: true,
  })
  const authorizationRejected =
    bootstrapError instanceof StreamBootstrapAuthorizationError

  useEffect(() => {
    if (!bootstrapError) {
      reportedBootstrapErrorRef.current = undefined
      return
    }
    if (reportedBootstrapErrorRef.current === bootstrapError) return

    reportedBootstrapErrorRef.current = bootstrapError
    reportOrderStreamUnreadFailure('bootstrap')
  }, [bootstrapError])

  useEffect(() => {
    if (!streamUnreadEnabled || !bootstrap || authorizationRejected) {
      return
    }

    let isCancelled = false
    let intervalId: ReturnType<typeof setInterval> | null = null
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let channel: StreamChannelType | null = null
    let clientDisconnected = false

    const sessionKey = `${bootstrap.user.id}:${bootstrap.token}`
    if (retrySessionRef.current !== sessionKey) {
      retrySessionRef.current = sessionKey
      retryAttemptRef.current = 0
      terminalSessionRef.current = null
    }
    if (terminalSessionRef.current === sessionKey) return

    // Match existing usage pattern in `OrderStreamChat` to avoid SDK signature mismatch.
    const streamClient = new StreamChat(bootstrap.apiKey)

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
      }
    }

    const closeClient = () => {
      if (clientDisconnected) return
      clientDisconnected = true
      if (channel) void channel.stopWatching().catch(() => null)
      void streamClient.disconnectUser().catch(() => null)
    }

    const scheduleRetry = () => {
      if (
        isCancelled ||
        retryTimer ||
        terminalSessionRef.current === sessionKey
      )
        return

      const failureCount = retryAttemptRef.current + 1
      retryAttemptRef.current = failureCount
      stopPolling()
      setUnreadState({ scope: unreadScope, value: null })

      const retryDelay = getStreamUnreadRetryDelay(failureCount)
      if (retryDelay === null) {
        terminalSessionRef.current = sessionKey
        stopPolling()
        closeClient()
        reportOrderStreamUnreadFailure('sync')
        return
      }

      retryTimer = setTimeout(() => {
        if (!isCancelled) {
          setRetryGeneration((generation) => generation + 1)
        }
      }, retryDelay)
    }

    const connectAndSync = async () => {
      try {
        await streamClient.connectUser(bootstrap.user, bootstrap.token)
        if (isCancelled || terminalSessionRef.current === sessionKey) return

        // Stream requires `connectUser` / `connectAnonymousUser` before creating a channel.
        channel = streamClient.channel(
          bootstrap.channel.type,
          bootstrap.channel.id,
        )
        await channel.watch()
        if (isCancelled || terminalSessionRef.current === sessionKey) {
          await channel.stopWatching().catch(() => null)
          return
        }

        const sync = () => {
          if (isCancelled || terminalSessionRef.current === sessionKey) return
          try {
            if (!channel) {
              scheduleRetry()
              return
            }
            setUnreadState({
              scope: unreadScope,
              value: channel.countUnread() > 0,
            })
            retryAttemptRef.current = 0
          } catch {
            scheduleRetry()
          }
        }

        sync()
        if (!retryTimer && terminalSessionRef.current !== sessionKey) {
          intervalId = setInterval(sync, 5000)
        }
      } catch {
        scheduleRetry()
      }
    }

    void connectAndSync()

    return () => {
      isCancelled = true
      stopPolling()
      if (retryTimer) clearTimeout(retryTimer)

      closeClient()
    }
  }, [
    authorizationRejected,
    bootstrap,
    retryGeneration,
    streamUnreadEnabled,
    unreadScope,
  ])

  // `null` means the external Stream state is unavailable; it is not a false unread result.
  return authorizationRejected
    ? null
    : orderID && streamUnreadEnabled && unreadState.scope === unreadScope
      ? unreadState.value
      : orderID && streamUnreadEnabled
        ? null
        : false
}
